import { useEffect } from 'react';

import { SaltclickChatbot } from '~/constants/ff.posthog';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUserSelector } from '~/hooks/useUserSelector';
import { fireHelpChatbotButtonTapped } from '~/posthogs/events';

interface IEmbeddedServiceBootstrap {
  settings: { language?: string };
  init: (orgId: string, deployment: string, siteUrl: string, options: { scrt2URL: string }) => void;
}

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    embeddedservice_bootstrap?: IEmbeddedServiceBootstrap;
  }
}

const SCRIPT_ID = 'saltclick-bootstrap';
const STYLE_ID = 'saltclick-visibility';
const VISIBLE_CLASS = 'saltclick-visible';

/**
 * Salesforce Embedded Messaging renders everything (button + chat) into a
 * `#embedded-messaging` container and can only be init'd once per page load,
 * so visibility is driven by a body class instead of unloading the widget.
 * The widget renders async after init; the CSS rule keeps it hidden on any
 * route where the Help page isn't mounted, even if init finishes after the
 * user has navigated away.
 */
const ensureVisibilityStyle = (): void => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent =
    `body:not(.${VISIBLE_CLASS}) #embedded-messaging { display: none !important; }`;
  document.head.appendChild(style);
};

/**
 * Embeds the Saltclick (Salesforce Embedded Messaging) chatbot on the Help
 * page. Gated by the `GOTU-4448-saltclick-chatbot` PostHog flag (state
 * targeting lives in the flag config). GOTU-5243.
 */
export const useSaltclickChat = (): void => {
  const flagEnabled = useFeatureFlag(SaltclickChatbot);
  const user = useUserSelector();
  // Logged-in users only — never show the widget when Help is reached from
  // login/sign-up (GOTU-5243).
  const enabled = flagEnabled && !!user;

  useEffect(() => {
    if (!enabled) return undefined;

    const orgId = import.meta.env.VITE_SALTCLICK_ORG_ID;
    const deployment = import.meta.env.VITE_SALTCLICK_DEPLOYMENT_NAME;
    const siteUrl = import.meta.env.VITE_SALTCLICK_SITE_URL;
    const scrt2URL = import.meta.env.VITE_SALTCLICK_SCRT2_URL;

    if (!orgId || !deployment || !siteUrl || !scrt2URL) {
      console.error('[Saltclick] Missing VITE_SALTCLICK_* environment config');
      return undefined;
    }

    ensureVisibilityStyle();
    document.body.classList.add(VISIBLE_CLASS);
    window.addEventListener('onEmbeddedMessagingConversationOpened', fireHelpChatbotButtonTapped);

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `${siteUrl}/assets/js/bootstrap.min.js`;
      script.onload = () => {
        const bootstrap = window.embeddedservice_bootstrap;
        if (!bootstrap) {
          console.error('[Saltclick] embeddedservice_bootstrap not available after script load');
          return;
        }
        try {
          bootstrap.settings.language = 'en_US';
          bootstrap.init(orgId, deployment, siteUrl, { scrt2URL });
        } catch (err) {
          console.error('Error loading Embedded Messaging: ', err);
        }
      };
      document.body.appendChild(script);
    }

    return () => {
      window.removeEventListener(
        'onEmbeddedMessagingConversationOpened',
        fireHelpChatbotButtonTapped,
      );
      document.body.classList.remove(VISIBLE_CLASS);
    };
  }, [enabled]);
};
